import React, { Component, Context } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridOptions, RowNode, GridReadyEvent, GridApi, ICellRendererParams, Column } from "ag-grid-community";
import { withRouter, RouteComponentProps } from "react-router";
import uniqby from "lodash.uniqby";
import merge from "lodash.merge";
// import LayoutContext, { ILayoutContext } from "../../contexts/layout/LayoutContext";
import UILoader from "../ui/UILoader";
import { DEFAULT_GRID_OPTIONS } from "./defaultOptions";
import { getDateColDef, ID_COL_DEF } from "./columns";
import { getUserCols } from "./columns/utils";
import { getActionsColumnDef } from "./columns/custom";
import UISearchInput from "../ui/UISearchInput";
import { CHECKBOX_COL_ID, isSystemColExtended } from "./colDef";
import UIRefresh from "../ui/UIRefresh";
import LayoutContext, { ILayoutContext } from "../../contexts/layout/LayoutContext";
import { QuickViewRenderer } from "./renderers/QuickViewRenderer";
import CustomTooltip from "./CustomTooltip";
import { IActionable, CustomAction } from "../../models/actions";
import "./grid.scss";
const queryString = require("query-string");

type GridProps = {
	columnDefs: ColDef[];
	rowData: any[];
	loading: boolean;
	isAdmin: boolean;
	exportFilename?: string;
	withUserCols?: boolean;
	omitCreatedCol?: boolean;
	gridOptions?: GridOptions;
	noDataMsg?: string;
	entityName?: string;
	displayTitle?: boolean;
	customTitle?: React.ReactNode | string;
	actionProps?: IActionable<any> & { customActions?: CustomAction[] };
	getCustomActions?: (data) => CustomAction[];
	actionsColWidth?: number;
	getTooltipDisplay?: (data, long?: boolean) => string;
	onRefresh?: () => Promise<any>;
	getItemInfoData?: (data) => Promise<any>;
	getItemEditData?: (data) => Promise<any>;
	getItemInfoDataCols?: (data) => Promise<ColDef[]>;
	additionalToolbarContent?: React.ReactNode;
	onGridReady?: (e: GridReadyEvent) => any;
};

type GridState = {
	loading: boolean;
	refreshing: boolean;
	columnDefs: ColDef[];
	searchQuery: string;
	selected: any[];
	selectedNodes: RowNode[];
	api: GridApi;
	detailViewOpen: boolean;
	detailData: any;
	detailColumnDefs: ColDef[];
	detailRendererParams: ICellRendererParams;
};

export class Grid extends Component<GridProps & RouteComponentProps, GridState> {
	static contextType: Context<ILayoutContext> = LayoutContext;
	private columnsSet: boolean = false;
	private queryStringParsed: any;

	state = {
		loading: true,
		refreshing: false,
		columnDefs: [],
		searchQuery: "",
		selected: [],
		selectedNodes: [],
		api: null,
		detailViewOpen: false,
		detailData: null,
		detailColumnDefs: [],
		detailRendererParams: null,
	};

	componentDidMount() {
		this.setColumns();
		this.queryStringParsed = queryString.parse(this.props.location.search);
		if (this.queryStringParsed?.gridQuery) this.setState({ searchQuery: this.queryStringParsed.gridQuery });
	}

	componentDidUpdate(prevProps: GridProps, prevState: GridState, snapshot) {
		if (
			prevProps.columnDefs !== this.props.columnDefs ||
			prevProps.columnDefs.length !== this.props.columnDefs.length
		) {
			this.setColumns(true);
		}
		this.handleOverlay(this.state.loading || this.props.loading || this.state.refreshing);
		if (
			(!this.props.loading && this.props.loading !== prevProps.loading) ||
			prevProps.rowData !== this.props.rowData
		) {
			this.handleFilter();
		}
	}

	handleFilter() {
		if (!this.queryStringParsed.filterCol || !this.queryStringParsed.filterVal) {
			return;
		}

		const api: GridApi = this.state.api;
		if (!api) {
			setTimeout(() => {
				this.handleFilter();
			}, 400);
			return;
		}
		const filterInstance = api.getFilterInstance(this.queryStringParsed.filterCol);
		if (!filterInstance) {
			setTimeout(() => {
				this.handleFilter();
			}, 400);
			return;
		}

		filterInstance.setModel({
			filterType: "text",
			type: "equals",
			filter: this.queryStringParsed.filterVal,
		});
		api.onFilterChanged();
	}

	handleOverlay(loading: boolean) {
		if (!this.state.api) {
			setTimeout(() => {
				this.handleOverlay(loading);
			}, 0);
			return;
		}
		if (loading) {
			this.state.api.showLoadingOverlay();
			return;
		}
		if (!this.props.rowData || !this.props.rowData.length) {
			this.state.api.hideOverlay();
			setTimeout(() => {
				if (!loading) this.state.api.showNoRowsOverlay();
			}, 0);
		} else {
			this.state.api.hideOverlay();
		}
	}

	setColumns(update?: boolean) {
		console.log("this.props.isAdmin:", this.props.isAdmin);
		if (!update && this.columnsSet) return;
		this.columnsSet = true;
		const createdAtCol: ColDef[] = this.props.omitCreatedCol
			? []
			: [
					{
						field: "created_at",
						colId: "created_at",
						headerName: "Created",
						headerTooltip: "Created",
						...getDateColDef("created_at"),
					},
			  ];
		const extendedColDefs: ColDef[] = [
			...createdAtCol,
			...(this.props.withUserCols ? getUserCols(!this.props.isAdmin) : []),
			...this.props.columnDefs,
		];
		let columnDefs: (ColDef | any)[] = [
			{
				field: "id",
				colId: CHECKBOX_COL_ID,
				pinned: "left",
				width: 60,
				maxWidth: 60,
				checkboxSelection: true,
				headerCheckboxSelection: true,
				headerCheckboxSelectionFilteredOnly: true,
				hideInDetailView: true,
				suppressSizeToFit: true,
				filter: false,
			},
			ID_COL_DEF,
			getActionsColumnDef(
				{
					...this.props.actionProps,
					onEdit: this.props.actionProps?.onEdit
						? async (data) => {
								let d = data;
								if (this.props.getItemEditData) {
									const additional = await this.props.getItemEditData(d);
									d = { ...d, ...additional };
								}
								return this.props.actionProps.onEdit(d);
						  }
						: undefined,

					onPreview: this.onPreview.bind(this),
				},
				this.props.getTooltipDisplay,
				undefined,
				this.props.columnDefs,
				this.props.actionProps?.customActions,
				this.props.getCustomActions,
				this.props.actionsColWidth
			),
			...extendedColDefs,
		];

		columnDefs = uniqby(columnDefs, "colId");
		this.setState({ loading: false, columnDefs });
	}

	async onPreview(data, params: ICellRendererParams): Promise<any> {
		console.log("Grid.onPreview(): data:", data);
		let infoData = {};
		let detailColumnDefs = this.state.columnDefs;
		let additionalCols: ColDef[] = [];
		if (this.props.getItemInfoData) {
			infoData = await this.props.getItemInfoData(data);
		}
		if (this.props.getItemInfoDataCols) {
			additionalCols = await this.props.getItemInfoDataCols(infoData);
		}
		const completeData = merge(data, infoData);
		detailColumnDefs = detailColumnDefs.concat(additionalCols);
		this.setState({
			detailData: completeData,
			detailRendererParams: params,
			detailViewOpen: true,
			detailColumnDefs: detailColumnDefs.concat(additionalCols),
		});
		return Promise.resolve();
	}

	exportCsv(filtered?: boolean) {
		const params = {
			fileName: this.props.exportFilename,
			onlySelected: !!filtered,
			columnKeys: this.state.columnDefs
				.filter((c: ColDef) => !isSystemColExtended(c as any))
				.map((c: ColDef) => c.colId),
		};
		this.state.api.exportDataAsCsv(params);
	}

	render() {
		if (this.state.loading) return <UILoader display={true} />;

		return (
			<>
				<div className="w-100 d-flex flex-column flex-md-row justify-content-between mb-1">
					{this.props.displayTitle && !!this.props.entityName && (
						<h2 className="mb-0 pb-0">{this.props.entityName}</h2>
					)}
					{this.props.customTitle}
					<div className="d-flex flex-column flex-lg-row align-items-end">
						{this.props.additionalToolbarContent}
						<UISearchInput
							className="narrow wider order-1 order-lg-0 mb-2-mobile-tablet"
							onSearch={(searchQuery) => {
								if (this.state.api) {
									this.state.api.setQuickFilter(searchQuery?.trim());
								}
							}}
							defaultValue={this.state.searchQuery}
						/>
						<div className="d-flex justify-content-between ml-2 order-0 order-lg-1 mb-2-mobile-tablet">
							<button
								className="btn btn-link btn-small mt-2-mobile mr-2"
								onClick={() => this.exportCsv()}
							>
								Export All
							</button>
							<button
								className="btn btn-secondary btn-small mt-2-mobile"
								disabled={this.state.selected.length === 0}
								title={
									this.state.selected.length
										? `Export ${this.state.selected.length}`
										: "Select rows to export"
								}
								onClick={() => this.exportCsv(true)}
							>
								Export Selected ({this.state.selected.length})
							</button>
							{!!this.props.onRefresh && (
								<UIRefresh
									disabled={this.state.loading}
									onRefresh={async () => {
										this.setState({ refreshing: true });
										await this.props.onRefresh();
										this.setState({ refreshing: false });
									}}
									className="ml-2 mt-2-mobile"
									refreshTitle={
										this.props.entityName ? `Refresh ${this.props.entityName}` : undefined
									}
								/>
							)}
						</div>
					</div>
				</div>
				<div className="ag-theme-material" style={{ width: "100%" }}>
					<AgGridReact
						columnDefs={this.state.columnDefs}
						rowData={this.props.rowData}
						gridOptions={{ ...DEFAULT_GRID_OPTIONS(this), ...(this.props.gridOptions || {}) }}
						onGridReady={(e: GridReadyEvent) => {
							this.setState({ api: e.api });
							if (this.context.settings.isDesktopRes) {
								const colIds: string[] = e.columnApi
									.getAllColumns()
									.filter((c: Column) => !c.getColDef().suppressAutoSize)
									.map((c: Column) => c.getColId());
								e.columnApi.autoSizeColumns(colIds);
								// e.columnApi.autoSizeColumns(); // sizeColumnsToFit
								// e.api.sizeColumnsToFit();
								// e.columnApi.getAllColumns().filter((c:ColDef)=> !c.suppressAutoSize)
							}
							if (this.state.searchQuery) e.api.setQuickFilter(this.state.searchQuery?.trim());
							if (this.props.onGridReady) {
								this.props.onGridReady(e);
							}
						}}
						tooltipShowDelay={2000}
						frameworkComponents={
							{
								// customTooltip: CustomTooltip, // TODO: dana temporarily disabe tooltip
							}
						}
					></AgGridReact>
					<div className="grid-status-bar py-3">
						<span className="mr-3">Total: {this.props.rowData?.length}</span>
						Selected: {this.state.selected.length}
					</div>

					{this.state.detailViewOpen && (
						<QuickViewRenderer
							{...this.state.detailRendererParams}
							hideBtn={true}
							colDefs={this.state.detailColumnDefs}
							data={this.state.detailData}
							onClose={() => {
								this.setState({ detailData: null, detailViewOpen: false, detailRendererParams: null });
							}}
							getTitle={this.props.getTooltipDisplay || ((data) => `#${data.id}`)}
						/>
					)}
				</div>
			</>
		);
	}
}

export default withRouter(Grid);
